import { useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  createClientCriticalPopulation,
  decideClientCriticalPopulation,
  deleteClientCriticalPopulation,
  getClientCriticalPopulations,
  getCriticalPopulationDimensions,
  getGetClientCriticalPopulationsQueryKey,
  getGetCriticalPopulationDimensionsQueryKey,
  previewClientCriticalPopulationTitle,
  updateClientCriticalPopulation,
} from 'api/generated/critical-populations/critical-populations';
import {
  getClientCriticalPopulationsMonitoringAvailability,
  getGetClientCriticalPopulationsMonitoringAvailabilityQueryKey,
} from 'api/generated/critical-populations-monitoring/critical-populations-monitoring';
import type {
  CriticalPopulationCreateRequest,
  CriticalPopulationDecisionRequest,
  CriticalPopulationDimensionDefinitionResponse,
  CriticalPopulationMonitoringAvailabilityResponse,
  CriticalPopulationResponse,
  CriticalPopulationTitlePreviewRequest,
  CriticalPopulationTitlePreviewResponse,
  CriticalPopulationUpdateRequest,
} from 'api/generated/schemas';
import type { ApiErrorWithMeta } from 'api/interceptors';

import { useCurrentUser } from 'hooks/api/use-current-user';

interface UseCriticalPopulationsArgs {
  clientId: number;
  enteringClassYear: number;
}

interface UseCriticalPopulationsMonitoringAvailabilityArgs
  extends UseCriticalPopulationsArgs {
  enabled: boolean;
}

interface UseCriticalPopulationTitlePreviewArgs {
  clientId: number;
  dimensions: CriticalPopulationTitlePreviewRequest['dimensions'];
  enabled: boolean;
}

export const TITLE_PREVIEW_DEBOUNCE_MS = 300;
const TITLE_PREVIEW_STALE_TIME_MS = 30_000;

interface UpdateCriticalPopulationVariables {
  id: number;
  data: CriticalPopulationUpdateRequest;
}

interface DecideCriticalPopulationVariables {
  id: number;
  data: CriticalPopulationDecisionRequest;
}

const useOktaUserId = (): string => {
  const user = useCurrentUser();

  return user.data?.oktaUserId ?? '';
};

export const useCriticalPopulations = ({
  clientId,
  enteringClassYear,
}: UseCriticalPopulationsArgs): UseQueryResult<
  CriticalPopulationResponse[],
  ApiErrorWithMeta
> => {
  const oktaUserId = useOktaUserId();
  const params = { enteringClassYear };

  return useQuery<CriticalPopulationResponse[], ApiErrorWithMeta>({
    queryKey: getGetClientCriticalPopulationsQueryKey(
      oktaUserId,
      clientId,
      params
    ),
    queryFn: ({ signal }) =>
      getClientCriticalPopulations(
        oktaUserId,
        clientId,
        params,
        undefined,
        signal
      ),
    enabled: !!oktaUserId && Number.isFinite(clientId),
  });
};

export const useCriticalPopulationsMonitoringAvailability = ({
  clientId,
  enteringClassYear,
  enabled,
}: UseCriticalPopulationsMonitoringAvailabilityArgs): UseQueryResult<
  CriticalPopulationMonitoringAvailabilityResponse[],
  ApiErrorWithMeta
> => {
  const oktaUserId = useOktaUserId();
  const params = { enteringClassYear };

  return useQuery<
    CriticalPopulationMonitoringAvailabilityResponse[],
    ApiErrorWithMeta
  >({
    queryKey: getGetClientCriticalPopulationsMonitoringAvailabilityQueryKey(
      oktaUserId,
      clientId,
      params
    ),
    queryFn: ({ signal }) =>
      getClientCriticalPopulationsMonitoringAvailability(
        oktaUserId,
        clientId,
        params,
        undefined,
        signal
      ),
    enabled: enabled && !!oktaUserId && Number.isFinite(clientId),
  });
};

export const useCriticalPopulationDimensions = (): UseQueryResult<
  CriticalPopulationDimensionDefinitionResponse[],
  ApiErrorWithMeta
> => {
  const oktaUserId = useOktaUserId();

  return useQuery<
    CriticalPopulationDimensionDefinitionResponse[],
    ApiErrorWithMeta
  >({
    queryKey: getGetCriticalPopulationDimensionsQueryKey(oktaUserId),
    queryFn: ({ signal }) =>
      getCriticalPopulationDimensions(oktaUserId, undefined, signal),
    enabled: !!oktaUserId,
    staleTime: Infinity,
  });
};

const normalizeDimensions = (
  dimensions: CriticalPopulationTitlePreviewRequest['dimensions']
): CriticalPopulationTitlePreviewRequest['dimensions'] =>
  dimensions.map(({ attribute, values }) => ({
    attribute,
    values: [...(values ?? [])].sort((a, b) => a.localeCompare(b)),
  }));

export const useCriticalPopulationTitlePreview = ({
  clientId,
  dimensions,
  enabled,
}: UseCriticalPopulationTitlePreviewArgs): UseQueryResult<
  CriticalPopulationTitlePreviewResponse,
  ApiErrorWithMeta
> => {
  const oktaUserId = useOktaUserId();
  const input = useMemo(
    () =>
      JSON.stringify({ dimensions: normalizeDimensions(dimensions), enabled }),
    [dimensions, enabled]
  );
  const [debouncedInput, setDebouncedInput] = useState(input);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => setDebouncedInput(input),
      TITLE_PREVIEW_DEBOUNCE_MS
    );

    return () => clearTimeout(timeoutId);
  }, [input]);

  const debounced: {
    dimensions: CriticalPopulationTitlePreviewRequest['dimensions'];
    enabled: boolean;
  } = JSON.parse(debouncedInput);

  return useQuery<CriticalPopulationTitlePreviewResponse, ApiErrorWithMeta>({
    queryKey: [
      `/api/${oktaUserId}/clients/${clientId}/critical-populations/title-preview`,
      debounced.dimensions,
    ],
    queryFn: ({ signal }) =>
      previewClientCriticalPopulationTitle(
        oktaUserId,
        clientId,
        { dimensions: debounced.dimensions },
        undefined,
        signal
      ),
    enabled: debounced.enabled && !!oktaUserId && Number.isFinite(clientId),
    retry: false,
    staleTime: TITLE_PREVIEW_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
};

const useInvalidateCriticalPopulations = (clientId: number) => {
  const queryClient = useQueryClient();
  const oktaUserId = useOktaUserId();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetClientCriticalPopulationsQueryKey(oktaUserId, clientId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetClientCriticalPopulationsMonitoringAvailabilityQueryKey(
          oktaUserId,
          clientId
        ),
      }),
    ]);
};

export const useCreateCriticalPopulation = (
  clientId: number
): UseMutationResult<
  CriticalPopulationResponse,
  ApiErrorWithMeta,
  CriticalPopulationCreateRequest
> => {
  const oktaUserId = useOktaUserId();
  const invalidate = useInvalidateCriticalPopulations(clientId);

  return useMutation<
    CriticalPopulationResponse,
    ApiErrorWithMeta,
    CriticalPopulationCreateRequest
  >({
    mutationFn: (data) =>
      createClientCriticalPopulation(oktaUserId, clientId, data),
    onSettled: invalidate,
  });
};

export const useUpdateCriticalPopulation = (
  clientId: number
): UseMutationResult<
  CriticalPopulationResponse,
  ApiErrorWithMeta,
  UpdateCriticalPopulationVariables
> => {
  const oktaUserId = useOktaUserId();
  const invalidate = useInvalidateCriticalPopulations(clientId);

  return useMutation<
    CriticalPopulationResponse,
    ApiErrorWithMeta,
    UpdateCriticalPopulationVariables
  >({
    mutationFn: ({ id, data }) =>
      updateClientCriticalPopulation(oktaUserId, clientId, id, data),
    onSettled: invalidate,
  });
};

export const useDecideCriticalPopulation = (
  clientId: number
): UseMutationResult<
  CriticalPopulationResponse,
  ApiErrorWithMeta,
  DecideCriticalPopulationVariables
> => {
  const oktaUserId = useOktaUserId();
  const invalidate = useInvalidateCriticalPopulations(clientId);

  return useMutation<
    CriticalPopulationResponse,
    ApiErrorWithMeta,
    DecideCriticalPopulationVariables
  >({
    mutationFn: ({ id, data }) =>
      decideClientCriticalPopulation(oktaUserId, clientId, id, data),
    onSettled: invalidate,
  });
};

export const useDeleteCriticalPopulation = (
  clientId: number
): UseMutationResult<void, ApiErrorWithMeta, number> => {
  const oktaUserId = useOktaUserId();
  const invalidate = useInvalidateCriticalPopulations(clientId);

  return useMutation<void, ApiErrorWithMeta, number>({
    mutationFn: (id) =>
      deleteClientCriticalPopulation(oktaUserId, clientId, id),
    onSettled: invalidate,
  });
};
